import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { MdClose } from 'react-icons/md';

import { DealerSearchContext } from '../../contexts/DealerSearchContext';
import { LocaleContext } from '../../contexts/LocaleContext';

import { Dealer as AlgoliaDealer } from '../../models/algolia/Dealer';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import LinkComponent from '@repo/ui-components/LinkComponent';
import breakpoints from '@repo/ui-components/breakpoints';
import useBodyScrollLock, { ScrollLockState } from '../hooks/useBodyScrollLock';
import { SiteSettingsContext } from '../../contexts/SiteSettingsContext';

import DealerSearch from './DealerSearch';
import { stripFormattingFromPhoneNumber } from '../../utils/stripFormattingFromPhoneNumber';

interface ModalProps {
  active: boolean;
}

const Modal = styled.div<ModalProps>`
  background-color: rgba(0, 0, 0, 0.65);
  position: fixed;
  z-index: 4;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: ${({ active }) => (active ? 'block' : 'none')};
`;

const ModalInner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  border-radius: 5px;
  width: 100%;
  max-width: 20rem;
  padding: 1rem;
  @media (min-width: ${breakpoints.sm}) {
    max-width: 30rem;
  }

  h2 {
    margin: 0;
    margin-bottom: 1rem;
  }
  p {
    margin: 0;
    font-size: 1rem;
    margin-bottom: 1rem;
  }
`;

const Header = styled.div`
  border-bottom: 1px solid #dddddd;
`;

const Heading = styled.p`
  display: flex;

  span {
    flex-grow: 1;
    font-weight: bold;
    font-size: 1.5em;
    line-height: 1.15;
    margin: 0 0 0.25em;
    color: ${({ theme }) => theme.colors.headline};
  }

  button {
    padding: 0;
    padding-left: 1rem;
    margin: 0;
    border: none;
    background: none;
    cursor: pointer;
  }
`;

const SearchWrapper = styled.div`
  padding-top: 1rem;
`;

const ScrollContainer = styled.div`
  max-height: 80vh;
  overflow-y: auto;
`;

export interface PlanUnavailableComponentProps {
  storedDealer: {
    name: string;
  };
  planType: string;
}

function PlanUnavailableComponent({
  storedDealer,
  planType,
}: Readonly<PlanUnavailableComponentProps>) {
  const { tt } = useUiComponentsInternationalContext();
  const { currency } = useContext(LocaleContext);
  const { name } = storedDealer;
  const { siteSettings } = useContext(SiteSettingsContext);

  return (
    <div>
      <p>
        <Heading>{tt(s => s.dealerCommon.planUnavailableHeader)}</Heading>
      </p>
      <p>
        {tt(s => s.dealerCommon.planUnavailableDescription, {
          planType,
          name: <strong>{name}</strong>,
        })}
      </p>
      <p>
        {tt(s => s.dealerCommon.planUnavailableCallTip, {
          phone: (
            <LinkComponent
              href={`tel:+${stripFormattingFromPhoneNumber(
                siteSettings.phone
              )}`}
              style={{
                whiteSpace: 'nowrap',
              }}
              className="phone"
            >
              {siteSettings.phone}
            </LinkComponent>
          ),
        })}
      </p>
      <p>
        <LinkComponent
          to={
            currency === 'CAD'
              ? '/en-ca/customer-resources'
              : '/customer-resources'
          }
          onClick={() => {
            document.body.style.overflow = 'auto';
          }}
        >
          {tt(s => s.dealerCommon.visitCustomerResourcePageLink)}
        </LinkComponent>
      </p>
    </div>
  );
}

interface DealerSearchContextProps {
  dealerSearchResults: AlgoliaDealer[];
  setDealerGateModalActive: React.Dispatch<React.SetStateAction<boolean>>;
  storedDealer: AlgoliaDealer | null;
}

export interface DealerGateModalProps {
  active?: boolean;
  planUnavailable?: boolean;
  planType?: string;
  closeButtonEnabled?: boolean;
}

function DealerGateModal({
  active = false,
  planUnavailable = false,
  planType = 'Default plan type',
  closeButtonEnabled = false,
}: Readonly<DealerGateModalProps>) {
  useBodyScrollLock(active ? ScrollLockState.LOCK : ScrollLockState.UNLOCK);
  const { storedDealer, dealerSearchResults, setDealerGateModalActive } =
    useContext<DealerSearchContextProps>(DealerSearchContext);

  const { tt } = useUiComponentsInternationalContext();

  const multipleSearchResults = dealerSearchResults?.length > 1;
  const noDealerSearchResults = dealerSearchResults?.length === 0;

  // if back button is pressed, close modal
  useEffect(() => {
    const handlePopState = () => {
      setDealerGateModalActive(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [setDealerGateModalActive]);

  return (
    <Modal active={active}>
      <ModalInner>
        <ScrollContainer>
          <Header>
            <Heading>
              <span>{tt(s => s.dealerCommon.selectLocalDealer)}</span>
              {closeButtonEnabled && (
                <button
                  type="button"
                  onClick={() => {
                    setDealerGateModalActive(false);
                  }}
                  aria-label={tt(
                    s => s.dealerCommon.closeDealerSearchAriaLabel
                  )}
                >
                  <MdClose size={32} className="fill-primary" />
                </button>
              )}
            </Heading>
            <p>{tt(s => s.dealerCommon.toProvideRelevantInfo)}</p>
          </Header>
          <SearchWrapper>
            {active && <DealerSearch displayMode="compact" />}
          </SearchWrapper>
          {planUnavailable &&
            !multipleSearchResults &&
            !noDealerSearchResults && (
              <div>
                <PlanUnavailableComponent
                  planType={planType}
                  storedDealer={storedDealer}
                />
              </div>
            )}
        </ScrollContainer>
      </ModalInner>
    </Modal>
  );
}

export default DealerGateModal;
